// Importaciones de React, librerías de animación y enrutamiento.
import React from 'react';
import 'animate.css'; // Librería para las animaciones CSS.
import { useInView } from 'react-intersection-observer'; // Hook para detectar la visibilidad de elementos.
import { Link } from 'react-router-dom'; // Para crear enlaces de navegación.
import imagenFondo from '../img/uno.jpg'; // Imagen de fondo para la sección.

/**
 * Componente InteligenciaArtificialCard.
 * Aunque su nombre sugiere una tarjeta, este componente renderiza una sección completa
 * que describe los servicios de Inteligencia Artificial de la empresa, divididos en
 * "IA Generativa" y "IA Aplicada". Anima cada parte de la sección por separado.
 */
const InteligenciaArtificialCard = () => {
  // Se utiliza un hook `useInView` para cada parte del componente que se desea animar de forma independiente.
  // `triggerOnce: true` asegura que la animación solo se ejecute la primera vez que el elemento es visible.
  
  // Observer para la cabecera.
  const [headerRef, inViewHeader] = useInView({ triggerOnce: true, threshold: 0.1 });
  // Observer para la descripción.
  const [descriptionRef, inViewDescription] = useInView({ triggerOnce: true, threshold: 0.1 });
  // Observer para la sección de IA Generativa.
  const [generativeRef, inViewGenerative] = useInView({ triggerOnce: true, threshold: 0.1 });
  // Observer para la sección de IA Aplicada.
  const [appliedRef, inViewApplied] = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    // Contenedor principal con una imagen de fondo fija.
    <div 
      className="flex justify-center items-center min-h-screen bg-gray-100 text-black p-6 bg-cover bg-center"
      style={{ 
        backgroundImage: `url(${imagenFondo})`,
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Contenedor de la tarjeta principal con sombra y bordes redondeados. */}
      <div className="max-w-6xl bg-white rounded-3xl shadow-2xl overflow-hidden transform transition-transform hover:scale-105 duration-300 ease-in-out">
        
        {/* Cabecera de la sección. */}
        <div 
          ref={headerRef} // Se asigna la referencia al observer.
          // Se aplican las clases de `animate.css` condicionalmente cuando `inViewHeader` es true.
          className={`p-8 text-center bg-gradient-to-r from-[#061C5B] to-[#203CE0] ${inViewHeader ? 'animate__animated animate__zoomIn' : ''}`}
        >
          <h1 className="text-4xl font-bold text-white">
            Inteligencia Artificial Empresarial - IA
          </h1>
        </div>

        {/* Contenido principal. */}
        <div className="p-8 space-y-12 text-black">
          
          {/* Párrafo de descripción. */}
          <div 
            ref={descriptionRef}
            className={`text-center text-xl ${inViewDescription ? 'animate__animated animate__fadeInUp' : ''}`}
          >
            Nos especializamos en dos áreas de estudio de la IA, ofreciendo una plataforma unificada donde pueden integrar diferentes Modelos de IA (públicos y empresariales) para resolver problemas particulares.
          </div>

          {/* Grid para las dos áreas de IA. */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            
            {/* Sección IA Generativa. */}
            <div 
              ref={generativeRef}
              className={`p-8 rounded-lg shadow-lg bg-white ${inViewGenerative ? 'animate__animated animate__fadeInLeft animate__delay-1s' : ''}`}
            >
              <h2 className="text-2xl font-semibold text-[#061C5B]">IA Generativa</h2>
              <p className="mt-6 text-lg text-black">
                Trabajamos en conjunto para el desarrollo y entrenamiento de asistentes virtuales que pueden interactuar con sus colaboradores, proveedores y clientes en chats o voz para atender requerimientos repetitivos donde el humano no genera valor, permitiendo automatizar varios procesos.
              </p>
            </div>

            {/* Sección IA Aplicada. */}
            <div 
              ref={appliedRef}
              className={`p-8 rounded-lg shadow-lg bg-white ${inViewApplied ? 'animate__animated animate__fadeInRight animate__delay-1s' : ''}`}
            >
              <h2 className="text-2xl font-semibold text-[#061C5B]">IA Aplicada</h2>
              <p className="mt-6 text-lg text-black">
                Este tipo de IA les ayudará al análisis de datos, utilizando algoritmos de ML específicos para atender casos de uso como la generación de forecast de ventas, análisis de documentos para reducir tiempos de revisión, detección de malware, entre otros algoritmos.
              </p>
            </div>
          </div>

          {/* Botón de llamada a la acción. */}
          <div className="flex justify-center">
            <Link to={'/consultas'}>
              <button className="bg-blue-600 text-white py-3 px-6 rounded-full shadow-md hover:bg-blue-700 transition-all duration-300 transform hover:scale-110 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                Conversemos
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Exporta el componente.
export default InteligenciaArtificialCard;
